import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next"; // <-- Import useTranslation

interface Blog {
    date: string;
    title: string;
    img: string;
}

const mockApiResponse = [
    {
        name: "1914 translation by H. Rackham",
        named: {
            title: [
                { "1": "1914 translation by H. Rackham" },
                { "2": "ترجمة عام 1914 بواسطة هـ. راكهام" },
                { "3": "H. Rackham کا 1914 ترجمہ" },
            ],
        },
        id: 1,
        image: "http://admin.cargooapp.com/uploads/image/article_image/20251128T073646904Z056595.jpg",
        createdAt: "2025-11-28T06:12:43.050Z",
    },
    {
        name: "Logistics Safety Standards",
        named: {
            title: [
                { "1": "Logistics Safety Standards" },
                { "2": "معايير السلامة اللوجستية" },
            ],
        },
        id: 2,
        image: "/cargo/latestNews/blog2.jpg",
        createdAt: "2025-11-29T10:20:00.000Z",
    },
    {
        name: "On-Time Freight Delivery",
        named: {
            title: [
                { "1": "On-Time Freight Delivery" },
                { "2": "تسليم الشحن في الوقت المحدد" },
            ],
        },
        id: 3,
        image: "/cargo/latestNews/blog3.jpg",
        createdAt: "2025-11-30T08:00:00.000Z",
    },
    {
        name: "Warehouse Optimization",
        named: {
            title: [
                { "1": "Warehouse Optimization" },
                { "2": "تحسين المستودعات" },
            ],
        },
        id: 4,
        image: "/cargo/latestNews/blog4.jpg",
        createdAt: "2025-12-01T12:30:00.000Z",
    },
];

const LatestNews: React.FC = () => {
    // 1. Initialize translation hook
    const { t, i18n } = useTranslation();

    const [blogs, setBlogs] = useState<Blog[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Get current language and derived properties
    const currentLang = i18n.language;
    const isArabic = currentLang === "ar";
    // Map Arabic ('ar') to ID '2', and use default '1' otherwise (based on your API sample)
    const langId = isArabic ? "2" : "1";

    // Set text direction
    const dir = isArabic ? "rtl" : "ltr";

    useEffect(() => {
        const fetchBlogs = async () => {
            setIsLoading(true);
            try {
                const res = await fetch(
                    "https://admin.cargooapp.com/api/articles/list",
                );

                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }

                const json = await res.json();
                const apiData = json.data; // ✅ FIX

                const mappedBlogs: Blog[] = apiData
                    .slice(0, 4)
                    .map((item: any) => {
                        let localizedTitle = item.name ?? "Untitled Article";

                        if (Array.isArray(item?.named?.title)) {
                            const titleEntry = item.named.title.find(
                                (entry: any) => entry[langId],
                            );

                            if (titleEntry?.[langId]) {
                                localizedTitle = titleEntry[langId];
                            }
                        }

                        return {
                            date: item.createdAt
                                ? new Date(item.createdAt).toLocaleDateString(
                                      "en-US",
                                      {
                                          year: "numeric",
                                          month: "short",
                                          day: "numeric",
                                      },
                                  )
                                : "Unknown Date",
                            title: localizedTitle,
                            img: item.image || "/cargo/latestNews/default.jpg",
                        };
                    });

                setBlogs(mappedBlogs);
            } catch (error) {
                console.error("Error fetching blogs:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchBlogs();
    }, [langId]);

    return (
        // 2. Add dir attribute for RTL support
        <>
            {blogs.length > 0 && (
                <section
                    dir={isArabic ? "rtl" : "ltr"}
                    className="clip-top-shape mt-28 w-full overflow-hidden bg-[#F2F2F2] pb-20 pt-28 font-stan"
                >
                    {/* --- Header Section --- */}
                    <div className="px-6 md:px-12">
                        <p className="mb-6 flex w-full items-center justify-start gap-2 text-xl font-bold text-[#64748B]">
                            <img
                                src="/cargo/herosection/right_arrow_color.svg"
                                alt="colored arrow"
                                // Rotate arrow for Arabic
                                className={`h-4 w-4 ${isArabic ? "rotate-180" : ""}`}
                            />
                            {/* 3. Localize Static Text (use t('key')) */}
                            {t("latestNews").toUpperCase()}
                        </p>

                        {/* <p className="mb-8 flex w-full flex-col text-left text-3xl font-bold leading-[1.1] sm:text-4xl md:text-5xl"> */}
                        <p
                            className={`mb-6 flex w-full items-center gap-2 text-3xl font-bold sm:text-4xl md:text-5xl ${isArabic ? "flex-row-reverse justify-end" : "justify-start"}`}
                        >
                            {/* Use dangerouslySetInnerHTML or t() for multi-line headers */}
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: t("insightsFromTheFieldTitle"),
                                }}
                            />
                        </p>
                    </div>

                    {/* --- Blog Cards Section --- */}
                    <section className="mt-20 w-full px-6 md:px-12">
                        {isLoading ? (
                            <p className="text-center text-gray-600">
                                {t("loadingBlogs")}
                            </p>
                        ) : blogs.length === 0 ? (
                            <p className="text-center text-gray-600">
                                {t("noRecentArticles")}
                            </p>
                        ) : (
                            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
                                {blogs.map((blog, i) => (
                                    <div
                                        key={i}
                                        className="flex flex-col overflow-hidden border border-gray-200 bg-white"
                                    >
                                        <img
                                            src={blog.img}
                                            alt={blog.title}
                                            className="h-56 w-full object-cover p-2"
                                            style={{
                                                clipPath:
                                                    "polygon(0 0, 100% 0, 100% 80%, 83% 100%, 0 100%)",
                                            }}
                                        />

                                        <div className="flex flex-col justify-between p-5">
                                            <p className="text-sm font-semibold tracking-widest text-[#64748B]">
                                                {blog.date.toUpperCase()}
                                            </p>

                                            <h2 className="mb-4 mt-2 text-lg font-bold leading-snug text-gray-900">
                                                {blog.title}
                                            </h2>

                                            <div className="flex items-center gap-2 border-t border-gray-200 pt-3 text-base font-semibold text-[#64748B]">
                                                {t("readMore")}
                                                <img
                                                    src="/cargo/herosection/right_arrow_color.svg"
                                                    alt="arrow"
                                                    className="h-4 w-4 text-[#00B9EC]"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </section>
            )}
        </>
    );
};

export default LatestNews;
